import React, { useCallback, useState } from 'react';
import OrderActionsMenu from './OrderActionsMenu';
import NotifyUserDialog from './NotifyUserDialog';
// 🪤 `apiFetch` does NOT prepend `API_BASE` — every caller supplies it, and a
//    bare `/api/…` here would stay relative and go to the CRA proxy (i.e.
//    another worktree's server and database) whenever `REACT_APP_API_URL` is
//    set. That misdiagnoses as a broken login, which is written up under
//    *Operational gotchas*.
import { apiFetch, API_BASE } from '../utils/api';
import { isLineFlagged } from '../constants/notifyReasons';

/**
 * NOTIFY USER — the `Actions ▾` menu on an order LINE.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 IT RESOLVES NO PERMISSIONS, EXACTLY LIKE `OrderActionsMenu` BELOW IT.
 * ═══════════════════════════════════════════════════════════════════════════
 * The CALLER decides whether to render it at all, and the caller writes the
 * gate beside the call. That is the same contract `components/OrderActionsMenu
 * .js` and `components/OrderTableView.js` both have, for the reason the
 * sanctioned-exception box gives: the moment a shared presentation component
 * can decide who sees an action, the surfaces that use it can disagree about
 * who may do what, and no page's gate is the answer any more.
 *
 * 🔑 **THE GATE EVERY CALLER MUST USE IS `useOrderAuthority().canManageOrder
 * (order)`** — `canManageDepartmentOrders` resolved in **the ORDER'S OWN
 * department**, which is verbatim what BOTH server handlers apply
 * (`requireDepartmentCapability(req, res, order.department_id,
 * 'canManageDepartmentOrders')`). Never `isBuyer`, never the ACTIVE
 * department's `capabilities` — the client must ask the question the server
 * asks, which is about the ORDER'S department however the list was built.
 * 🔻 The reason given here until 2026-08-12 was *"`GET /api/approvals/pending`
 * is not department-scoped at all, so To Approve routinely lists orders from a
 * department the viewer is not standing in"*. It is scoped now, site admin
 * excepted (`server/helpers/pendingApprovalsScope.js`); **the gate is unchanged
 * and switching it to `capabilities` would still be wrong.**
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 CLEARING IS BUYER-ONLY, AND THE REQUESTER GETS NO MENU ANYWHERE
 * ─────────────────────────────────────────────────────────────────────────────
 * `MyOrders.js` renders the line-flag MARK and no menu at all. The requester
 * cannot notify themselves and must not clear the buyer's flag — they are two
 * different objects, and the requester's action is dismissing the
 * NOTIFICATION, which already exists and works. `DELETE …/notify` applies the
 * SAME capability as the POST, so a surface that can set a flag can always
 * clear it; do not build one that can only set.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🚨 `no_requester` (409) — THE ONE RESPONSE THAT MUST NOT MARK THE LINE
 * ─────────────────────────────────────────────────────────────────────────────
 * When an order's requester account has been deleted the route answers 409 and
 * **writes nothing at all**: no flag, no notification, no audit row. So there
 * is deliberately NO optimistic update here — the row is repainted only from
 * the `item` the server returns. An optimistic tick would leave a flag on
 * screen that does not exist in the database and vanishes on the next refresh,
 * which reads as data loss rather than as a refused request.
 *
 * The other three refusals are ordinary 400s carrying their own message
 * (`notify_reasons_required`, `notify_note_required`, `notify_note_too_long`);
 * the dialog makes all three unreachable, and they are surfaced rather than
 * swallowed because a validation rule that can only be met by accident is one
 * nobody can debug.
 *
 * Props:
 *   order      — the order row/detail (needs `id`; the CALLER has already
 *                gated on it).
 *   item       — the order item row.
 *   onItemUpdated(item) — called with the SERVER'S updated item after either
 *                verb succeeds. The caller patches its own detail cache.
 *   testIdPrefix — namespaces every testid, so two surfaces rendering the same
 *                line cannot stamp one id. The `ColumnResizeHandle`
 *                `testIdPrefix` device, and the reason is the measured
 *                duplicate-testid defect class: `container.querySelector`
 *                returns the first match silently.
 *   compact    — forwarded to `OrderActionsMenu`; defaults TRUE here, because
 *                a line-item menu is the case that prop was added for.
 */
export default function OrderItemActionsMenu({
  order,
  item,
  onItemUpdated,
  testIdPrefix = 'order-item',
  compact = true,
  extraItems = [],
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const orderId = order?.id;
  const itemId = item?.id;
  const flagged = isLineFlagged(item);

  const notify = useCallback(async ({ reasons, note }) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/items/${itemId}/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reasons, note }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 🚨 The 409 lands here with everything else and writes NOTHING — see
        //    the docblock. The dialog stays open carrying the server's own
        //    sentence, which for `no_requester` explains that the account may
        //    have been deleted; inventing our own wording would drift from it.
        setError(body.error || 'Could not notify the requester.');
        return;
      }
      // Repaint from the SERVER's row, never from what we sent.
      if (body.item) onItemUpdated?.(body.item);
      setDialogOpen(false);
    } catch (err) {
      setError(err?.message || 'Could not notify the requester.');
    } finally {
      setBusy(false);
    }
  }, [orderId, itemId, onItemUpdated]);

  const clearFlag = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/items/${itemId}/notify`, {
        method: 'DELETE',
      });
      const body = await res.json().catch(() => ({}));
      // The route is idempotent and answers 200 even for an already-clear line,
      // so there is no double-click case to guard.
      if (res.ok && body.item) onItemUpdated?.(body.item);
    } catch {
      // Deliberately silent: clearing is a tidy-up on the buyer's own marker,
      // and a failed one leaves the flag visibly still there — the state IS the
      // error message. A toast here would be the only error banner in a
      // component that otherwise reports through the dialog.
    }
  }, [orderId, itemId, onItemUpdated]);

  const menuItems = [
    {
      key: 'notify',
      label: flagged ? 'Edit notification…' : 'Notify User…',
      onClick: () => { setError(null); setDialogOpen(true); },
      testId: `${testIdPrefix}-notify-${itemId}`,
      tone: 'progress',
      title: flagged
        ? 'Change the reasons or the note on this line'
        : 'Tell the requester about a problem with this line',
    },
    // Only offered on a line that is actually flagged — clearing an unflagged
    // line is a no-op the server accepts and a control the user cannot read a
    // meaning into.
    flagged && {
      key: 'clear-flag',
      label: 'Clear flag',
      onClick: clearFlag,
      testId: `${testIdPrefix}-clear-flag-${itemId}`,
      title: 'Remove the mark from this line. The requester keeps their notification.',
    },
    ...(extraItems || []),
  ];

  return (
    <>
      <OrderActionsMenu
        items={menuItems}
        compact={compact}
        label="Actions"
        ariaLabel={`Actions for ${item?.description || 'this line'}`}
        triggerTestId={`${testIdPrefix}-actions-${itemId}`}
        panelTestId={`${testIdPrefix}-actions-panel-${itemId}`}
      />
      <NotifyUserDialog
        open={dialogOpen}
        item={item}
        busy={busy}
        error={error}
        onSubmit={notify}
        onCancel={() => { if (!busy) { setDialogOpen(false); setError(null); } }}
      />
    </>
  );
}
